import React, { useEffect, useState } from "react";
import { Boxes, PlusCircle, SlidersHorizontal, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money, num, Spinner, PageHeader, Empty } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { FieldW, SelectBox } from "@/pages/Clients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const TXN_TINT = {
  Purchase: "text-emerald-400", "Opening Stock": "text-sky-400", "Order Consumption": "text-amber-400",
  Wastage: "text-rose-400", Expiry: "text-rose-400", Damage: "text-rose-300", "Manual Adjustment": "text-violet-400",
};

export default function Inventory() {
  const { user } = useAuth();
  const isClient = user.role === "client";
  const [ings, setIngs] = useState(null);
  const [txns, setTxns] = useState([]);
  const [recs, setRecs] = useState([]);
  const [brands, setBrands] = useState([]);
  const [dlg, setDlg] = useState(null); // 'opening' | 'adjust' | 'rec'
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (isClient && user.brand_id) {
      api.get(`/brands/${user.brand_id}/inventory`).then((r) => setIngs(r.data));
    } else {
      api.get("/ingredients").then((r) => setIngs(r.data));
    }
    api.get("/recommendations").then((r) => setRecs(r.data)).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    if (!isClient) api.get("/inventory/transactions").then((r) => setTxns(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openDlg = (type) => {
    if (type === "opening") setForm({ ingredient_id: ings[0]?.id, quantity: 0, internal_cost: 0, expiry_date: "", notes: "" });
    if (type === "adjust") setForm({ ingredient_id: ings[0]?.id, quantity_delta: 0, reason: "" });
    if (type === "rec") setForm({ brand_id: brands[0]?.id, ingredient_id: ings[0]?.id, recommended_opening_qty: 0, minimum_level: 0, reorder_threshold: 0, recommended_reorder_qty: 0, client_approved_qty: 0 });
    setDlg(type);
  };

  const save = async () => {
    setBusy(true);
    try {
      if (dlg === "opening") await api.post("/inventory/opening-stock", { ...form, quantity: Number(form.quantity), internal_cost: Number(form.internal_cost), expiry_date: form.expiry_date || null });
      if (dlg === "adjust") await api.post("/inventory/adjustment", { ...form, quantity_delta: Number(form.quantity_delta) });
      if (dlg === "rec") await api.post("/recommendations", Object.fromEntries(Object.entries(form).map(([k, v]) => [k, typeof v === "string" && k.endsWith("qty") ? Number(v) : v])));
      toast.success("Saved");
      setDlg(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!ings) return <Spinner />;
  const ingOpts = ings.map((i) => ({ value: i.id, label: i.name }));

  return (
    <div>
      <PageHeader title="Inventory" subtitle="One physical kitchen stock · every movement is a transaction">
        {!isClient && (
          <>
            <Button variant="outline" onClick={() => openDlg("opening")} data-testid="opening-stock-btn" className="gap-1.5"><PlusCircle className="h-4 w-4" /> Opening Stock</Button>
            {user.role !== "finance" && <Button variant="outline" onClick={() => openDlg("adjust")} data-testid="adjustment-btn" className="gap-1.5"><SlidersHorizontal className="h-4 w-4" /> Adjustment</Button>}
            <Button variant="outline" onClick={() => openDlg("rec")} data-testid="add-rec-btn" className="gap-1.5"><ClipboardList className="h-4 w-4" /> Recommendation</Button>
          </>
        )}
      </PageHeader>

      <Tabs defaultValue="stock">
        <TabsList className="mb-4">
          <TabsTrigger value="stock" data-testid="tab-stock">Stock</TabsTrigger>
          {!isClient && <TabsTrigger value="movements" data-testid="tab-movements">Movements</TabsTrigger>}
          <TabsTrigger value="recs" data-testid="tab-recs">Recommendations</TabsTrigger>
        </TabsList>

        <TabsContent value="stock">
          <div className="card-surface overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent border-border">
                <TableHead>Ingredient</TableHead><TableHead className="text-right">On Hand</TableHead>
                <TableHead className="text-right">Min</TableHead><TableHead className="text-right">Reorder</TableHead>
                {!isClient && <TableHead className="text-right">Stock Value</TableHead>}
              </TableRow></TableHeader>
              <TableBody>
                {ings.map((i) => (
                  <TableRow key={i.id} className="border-border" data-testid={`stock-row-${i.id}`}>
                    <TableCell className="font-medium text-white">{i.name}
                      <span className="block text-[11px] text-muted-foreground">{i.category}</span></TableCell>
                    <TableCell className={`text-right font-mono ${i.out_of_stock ? "text-rose-400" : i.reorder_required ? "text-orange-400" : "text-slate-200"}`}>
                      {num(i.current_quantity)} {i.purchase_unit}</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{num(i.minimum_stock)}</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{num(i.reorder_threshold)}</TableCell>
                    {!isClient && <TableCell className="text-right font-mono text-slate-300">{money(i.stock_value)}</TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {!isClient && (
          <TabsContent value="movements">
            {txns.length === 0 ? <Empty icon={Boxes} title="No transactions yet" /> : (
              <div className="card-surface overflow-x-auto">
                <Table>
                  <TableHeader><TableRow className="hover:bg-transparent border-border">
                    <TableHead>Date</TableHead><TableHead>Ingredient</TableHead><TableHead>Type</TableHead>
                    <TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Value</TableHead><TableHead>Note</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {txns.map((t) => (
                      <TableRow key={t.id} className="border-border">
                        <TableCell className="font-mono text-xs text-muted-foreground">{t.date}</TableCell>
                        <TableCell className="text-slate-200">{t.ingredient_name}</TableCell>
                        <TableCell className={`font-medium ${TXN_TINT[t.type] || "text-slate-300"}`}>{t.type}</TableCell>
                        <TableCell className={`text-right font-mono ${t.qty_change < 0 ? "text-rose-400" : "text-emerald-400"}`}>{t.qty_change > 0 ? "+" : ""}{num(t.qty_change)} {t.unit}</TableCell>
                        <TableCell className="text-right font-mono text-slate-300">{money(t.internal_value)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{t.note || t.reason || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        )}

        <TabsContent value="recs">
          {recs.length === 0 ? <Empty icon={ClipboardList} title="No recommendations" hint={isClient ? undefined : "Record recommended and client-approved quantities."} /> : (
            <div className="card-surface overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent border-border">
                  <TableHead>Ingredient</TableHead><TableHead className="text-right">Opening</TableHead><TableHead className="text-right">Min</TableHead>
                  <TableHead className="text-right">Reorder</TableHead><TableHead className="text-right">Approved</TableHead><TableHead className="text-right">On Hand</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {recs.map((r) => (
                    <TableRow key={r.id} className="border-border">
                      <TableCell className="text-white font-medium">{r.ingredient_name}</TableCell>
                      <TableCell className="text-right font-mono text-slate-300">{num(r.recommended_opening_qty)}</TableCell>
                      <TableCell className="text-right font-mono text-slate-300">{num(r.minimum_level)}</TableCell>
                      <TableCell className="text-right font-mono text-slate-300">{num(r.reorder_threshold)}</TableCell>
                      <TableCell className="text-right font-mono text-amber-300">{num(r.client_approved_qty)}</TableCell>
                      <TableCell className="text-right font-mono text-slate-400">{num(r.current_quantity)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader><DialogTitle>
            {dlg === "opening" && "Enter Opening Stock"}{dlg === "adjust" && "Inventory Adjustment"}{dlg === "rec" && "Inventory Recommendation"}
          </DialogTitle></DialogHeader>
          <div className="space-y-3">
            {dlg === "rec" && <FieldW label="Brand"><SelectBox value={form.brand_id} onChange={(v) => set("brand_id", v)} options={brands.map((b) => ({ value: b.id, label: b.name }))} /></FieldW>}
            <FieldW label="Ingredient"><SelectBox value={form.ingredient_id} onChange={(v) => set("ingredient_id", v)} options={ingOpts} testid="dlg-ingredient" /></FieldW>
            {dlg === "opening" && <>
              <FieldW label="Opening quantity (purchase units)"><Input type="number" data-testid="opening-qty" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} /></FieldW>
              <FieldW label="Internal cost (AED / unit)"><Input type="number" value={form.internal_cost} onChange={(e) => set("internal_cost", e.target.value)} /></FieldW>
              <FieldW label="Expiry date"><Input type="date" value={form.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} /></FieldW>
              <FieldW label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></FieldW>
            </>}
            {dlg === "adjust" && <>
              <FieldW label="Quantity delta (+/-)"><Input type="number" data-testid="adjust-delta" value={form.quantity_delta} onChange={(e) => set("quantity_delta", e.target.value)} /></FieldW>
              <FieldW label="Reason (required)"><Textarea rows={2} data-testid="adjust-reason" value={form.reason} onChange={(e) => set("reason", e.target.value)} /></FieldW>
            </>}
            {dlg === "rec" && <div className="grid grid-cols-2 gap-3">
              <FieldW label="Opening qty"><Input type="number" value={form.recommended_opening_qty} onChange={(e) => set("recommended_opening_qty", e.target.value)} /></FieldW>
              <FieldW label="Min level"><Input type="number" value={form.minimum_level} onChange={(e) => set("minimum_level", e.target.value)} /></FieldW>
              <FieldW label="Reorder threshold"><Input type="number" value={form.reorder_threshold} onChange={(e) => set("reorder_threshold", e.target.value)} /></FieldW>
              <FieldW label="Reorder qty"><Input type="number" value={form.recommended_reorder_qty} onChange={(e) => set("recommended_reorder_qty", e.target.value)} /></FieldW>
              <FieldW label="Client-approved qty" full><Input type="number" value={form.client_approved_qty} onChange={(e) => set("client_approved_qty", e.target.value)} /></FieldW>
            </div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="save-inv-btn" className="bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
